import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "../../components/argus/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Key, Trash2, Plus, Copy, Loader2, CheckCircle2, AlertTriangle, CreditCard, Lock, ExternalLink,
} from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";

const PLAN_BADGES = {
  free: { color: "text-ink/70", label: "Free" },
  pro: { color: "text-gold", label: "Pro" },
  enterprise: { color: "text-safe", label: "Enterprise" },
};

function BillingTab() {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const billing = params.get("billing");
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  const loadUsage = useCallback(async () => {
    try { setUsage((await api.get("/usage")).data); }
    catch (err) { console.warn("Settings: usage load failed", err); }
  }, []);

  useEffect(() => { loadUsage(); }, [loadUsage]);

  // Poll status after Stripe success redirect
  useEffect(() => {
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        const data = r.data || {};
        if (data.payment_status === "paid") {
          toast.success("Payment confirmed — you're on Pro.");
          setPolling(false);
          // strip session_id from URL
          params.delete("session_id"); params.delete("billing");
          setParams(params, { replace: true });
          loadUsage();
          return;
        }
        if (data.status === "expired" || attempts > 10) {
          toast.error("Payment didn't complete. Try again or contact security@argus.security.");
          setPolling(false);
          return;
        }
      } catch (err) {
        console.warn("Settings: billing status poll failed", err);
      }
      setTimeout(tick, 2000);
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const upgrade = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout", {
        package_id: "pro_monthly",
        origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout.");
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/portal", { return_url: window.location.origin });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal.");
    } finally {
      setBusy(false);
    }
  };

  const plan = usage?.plan || "free";
  const badge = PLAN_BADGES[plan] || PLAN_BADGES.free;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="billing-card">
      {billing === "cancelled" && (
        <div className="rounded-md bg-sev-medium/10 border border-sev-medium/30 p-3 flex gap-2 text-sm">
          <AlertTriangle size={16} className="text-sev-medium shrink-0 mt-0.5" />
          <span>Checkout cancelled. No charge made.</span>
        </div>
      )}
      {polling && (
        <div className="rounded-md bg-gold/10 border border-gold/30 p-3 flex gap-2 text-sm" data-testid="billing-polling">
          <Loader2 size={16} className="text-gold animate-spin shrink-0 mt-0.5" />
          <span>Confirming your payment with Stripe…</span>
        </div>
      )}
      <div className="rounded-xl border border-line bg-panel p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-display text-xl">Current plan</h3>
          <span className={`font-display text-2xl ${badge.color}`} data-testid="billing-current-plan">{badge.label}</span>
        </div>
        {usage && (
          <ul className="text-sm text-ink/80 space-y-1.5 mb-4">
            <li>Projects: <span className="font-mono">{usage.projects}</span> {usage.limits.projects != null && <span className="text-ink/50">/ {usage.limits.projects}</span>}</li>
            <li>Scans (last 30 days): <span className="font-mono">{usage.scans_30d}</span> {usage.limits.scans_per_month != null && <span className="text-ink/50">/ {usage.limits.scans_per_month}</span>}</li>
          </ul>
        )}
        {plan === "free" && (
          <>
            <p className="text-sm text-ink/70 mb-4">Upgrade for unlimited scans, 25 projects, PDF reports, API tokens, and priority security support.</p>
            <Button
              onClick={upgrade}
              disabled={busy}
              className="bg-gold hover:bg-gold2 text-paper h-12 font-semibold w-full"
              data-testid="upgrade-button"
            >
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CreditCard size={16} className="mr-2" />}
              Upgrade to Pro — $49/mo
            </Button>
            <p className="text-xs text-ink/40 mt-3">Billing handled by Stripe. Card data never touches ArgusSecure servers.</p>
          </>
        )}
        {plan === "pro" && (
          <>
            <div className="flex items-center gap-2 text-sm text-safe mb-4" data-testid="billing-pro-active">
              <CheckCircle2 size={16} /> You're on Pro. Update card, view invoices, or cancel anytime.
            </div>
            <Button
              onClick={openPortal}
              disabled={busy}
              variant="outline"
              className="w-full h-12 border-line bg-transparent text-ink hover:bg-raised font-semibold"
              data-testid="manage-billing-button"
            >
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ExternalLink size={16} className="mr-2" />}
              Manage Billing
            </Button>
            <p className="text-xs text-ink/40 mt-2 text-center mb-3">Opens the Stripe-hosted customer portal in a new page.</p>
            <button
              onClick={async () => {
                if (!confirm("Downgrade to Free? You'll lose Pro features at the end of the current billing period.")) return;
                setBusy(true);
                try {
                  const r = await api.post("/billing/cancel", { at_period_end: true });
                  toast.success(r.data?.message || "Subscription will end at the period close.");
                  loadUsage();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't cancel.");
                } finally {
                  setBusy(false);
                }
              }}
              className="text-xs text-ink/50 hover:text-sev-high underline underline-offset-2 w-full text-center"
              data-testid="downgrade-button"
              disabled={busy}
            >
              Downgrade to Free
            </button>
          </>
        )}
        {plan === "enterprise" && (
          <p className="text-sm text-ink/70">Enterprise plans are managed by your ArgusSecure lead.</p>
        )}
      </div>
    </div>
  );
}

function PasswordTab() {
  const [cur, setCur] = useState("");
  const [n1, setN1] = useState("");
  const [n2, setN2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (n1.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (n1 !== n2) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: n1 });
      toast.success("Password updated. Sign in again on other devices.");
      setCur(""); setN1(""); setN2("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change your password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-line bg-panel p-6 max-w-md space-y-4" data-testid="password-card">
      <h3 className="font-display text-xl flex items-center gap-2"><Lock size={16} className="text-gold" /> Change password</h3>
      <div>
        <Label className="text-xs uppercase tracking-wider text-ink/60">Current password</Label>
        <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required autoComplete="current-password" className="h-12 mt-1 bg-paper border-line" data-testid="pw-current" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-ink/60">New password</Label>
        <Input type="password" value={n1} onChange={(e) => setN1(e.target.value)} required autoComplete="new-password" className="h-12 mt-1 bg-paper border-line" data-testid="pw-new1" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-ink/60">Confirm new password</Label>
        <Input type="password" value={n2} onChange={(e) => setN2(e.target.value)} required autoComplete="new-password" className="h-12 mt-1 bg-paper border-line" data-testid="pw-new2" />
      </div>
      <Button type="submit" disabled={busy} className="bg-gold text-paper hover:bg-gold2 h-12 w-full font-semibold" data-testid="pw-submit">
        {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
        {busy ? "Updating…" : "Update password"}
      </Button>
      <p className="text-xs text-ink/50">Updating your password will sign you out on every other device.</p>
    </form>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [tokens, setTokens] = useState([]);
  const [audit, setAudit] = useState([]);
  const [name, setName] = useState("");
  const [newToken, setNewToken] = useState(null);

  const refresh = async () => {
    const [t, a] = await Promise.all([api.get("/tokens"), api.get("/audit-log")]);
    setTokens(t.data);
    setAudit(a.data);
  };

  useEffect(() => { refresh(); }, []);

  const createToken = async () => {
    if (!name.trim()) return;
    const r = await api.post("/tokens", { name });
    setNewToken(r.data.secret);
    setName("");
    refresh();
    toast.success("Token created. Copy it now — you won't see it again.");
  };

  const revoke = async (id) => {
    await api.delete(`/tokens/${id}`);
    refresh();
    toast.success("Token revoked.");
  };

  return (
    <AppShell title="Settings">
      <Tabs defaultValue="profile">
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="password" data-testid="tab-password">Security</TabsTrigger>
          <TabsTrigger value="tokens" data-testid="tab-tokens">API Tokens</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="rounded-xl border border-line bg-panel p-6 max-w-xl space-y-4" data-testid="profile-card">
            <div>
              <Label>Name</Label>
              <Input defaultValue={user?.name} className="h-12 mt-1" disabled />
            </div>
            <div>
              <Label>Email</Label>
              <Input defaultValue={user?.email} className="h-12 mt-1" disabled />
            </div>
            <div>
              <Label>Role</Label>
              <Input defaultValue={user?.role} className="h-12 mt-1" disabled />
            </div>
            <div className="text-xs text-ink/50">
              {user?.email_verified ? (
                <span className="inline-flex items-center gap-1 text-safe"><CheckCircle2 size={12} /> Email verified</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-sev-medium"><AlertTriangle size={12} /> Email not yet verified — check the banner above for a resend.</span>
              )}
            </div>
            <p className="text-xs text-ink/50 pt-2 border-t">SSO, member invites and role management land in the Enterprise plan.</p>
          </div>
        </TabsContent>

        <TabsContent value="password">
          <PasswordTab />
        </TabsContent>

        <TabsContent value="tokens">
          <div className="rounded-xl border border-line bg-panel p-6 max-w-2xl" data-testid="tokens-card">
            <h3 className="font-display text-xl mb-1">API tokens</h3>
            <p className="text-sm text-ink/60 mb-4">Use these to call the ArgusSecure API from CI or scripts.</p>

            {newToken && (
              <div className="mb-4 rounded-md bg-safe/10 border border-safe/30 p-3 flex items-center gap-2 text-sm" data-testid="new-token-banner">
                <Key size={16} className="text-safe" />
                <code className="font-mono break-all flex-1">{newToken}</code>
                <button onClick={() => { navigator.clipboard.writeText(newToken); toast.success("Copied."); }} className="text-ink/60 hover:text-ink">
                  <Copy size={14} />
                </button>
              </div>
            )}

            <div className="flex gap-2 mb-6">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Token name (e.g. CI)" className="h-12 flex-1" data-testid="token-name-input" />
              <Button onClick={createToken} className="bg-gold text-paper hover:bg-gold2 h-12" data-testid="create-token-button"><Plus size={16} className="mr-2" /> Create</Button>
            </div>

            {tokens.length === 0 ? (
              <p className="text-sm text-ink/50">No tokens yet.</p>
            ) : (
              <ul className="divide-y divide-line" data-testid="tokens-list">
                {tokens.map((t) => (
                  <li key={t.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{t.name}</div>
                      <div className="text-xs text-ink/50 font-mono">{t.prefix}…</div>
                    </div>
                    <button onClick={() => revoke(t.id)} className="text-sev-critical hover:opacity-70" data-testid={`revoke-${t.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <div className="rounded-xl border border-line bg-panel overflow-hidden max-w-3xl" data-testid="audit-card">
            <table className="w-full text-sm">
              <thead className="bg-raised text-ink/60 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5">When</th>
                  <th className="text-left px-4 py-2.5">Action</th>
                  <th className="text-left px-4 py-2.5">Detail</th>
                </tr>
              </thead>
              <tbody>
                {audit.length === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-6 text-center text-ink/50">No activity yet.</td></tr>
                ) : audit.map((e) => (
                  <tr key={e.id} className="border-t border-line dense-row">
                    <td className="px-4 font-mono text-xs">{new Date(e.at).toLocaleString()}</td>
                    <td className="px-4 font-mono text-xs">{e.action}</td>
                    <td className="px-4 text-xs text-ink/60 truncate max-w-[280px]">{JSON.stringify(e.detail)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="billing">
          <BillingTab />
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
